/**
 * Plans singleton: loads GET /api/plans once and exposes them by id.
 * Plans change only when a new coach PDF becomes a new migration, so one
 * fetch per page load is plenty.
 */

import { apiFetch } from './api';
import type { Plan } from './types';

const state = $state<{
    list: Plan[];
    loaded: boolean;
    loading: boolean;
    error: string | null;
}>({ list: [], loaded: false, loading: false, error: null });

let inflight: Promise<Plan[]> | null = null;

async function load(force = false): Promise<Plan[]> {
    if (state.loaded && !force) return state.list;
    if (inflight) return inflight;
    state.loading = true;
    state.error = null;
    inflight = (async () => {
        try {
            const list = await apiFetch<Plan[]>('/api/plans');
            state.list = list;
            state.loaded = true;
            return list;
        } catch (err) {
            state.error = err instanceof Error ? err.message : 'Ohjelmien haku epäonnistui';
            return [];
        } finally {
            state.loading = false;
            inflight = null;
        }
    })();
    return inflight;
}

function byId(id: string | null | undefined): Plan | undefined {
    if (!id) return undefined;
    return state.list.find((p) => p.id === id);
}

/** Plan title for pills; falls back to the raw id until plans have loaded. */
function titleOf(id: string | null | undefined): string {
    return byId(id)?.title ?? id ?? '';
}

export const plans = {
    get list(): Plan[] { return state.list; },
    get loaded(): boolean { return state.loaded; },
    get loading(): boolean { return state.loading; },
    get error(): string | null { return state.error; },
    load,
    byId,
    titleOf,
};
